/**
 * PortalDropdown — wrapper común que renderiza un listbox/popover en
 * `document.body` posicionado en `position: fixed` alineado bajo un
 * trigger. Patrón usado para escapar del `overflow-hidden` del card
 * padre detectado en el smoke E2E del CEO (2026-05-05) — el listbox
 * de los CustomDropdown de `AdminZonePicker` y de `CategoryAutocomplete`
 * quedaba clippado mostrando sólo la primera opción.
 *
 * **Decisión A V2** (createPortal nativo): se usa `createPortal` de
 * `react-dom` directamente. Coherente con la decisión A del baseline
 * T-BLS-UI-FIX que rechazó añadir Headless UI / Radix / Floating UI
 * como runtime dep. React DOM ya está en el bundle.
 *
 * **Decisión B V2** (cálculo de posición): `getBoundingClientRect()` del
 * trigger en `useLayoutEffect` cuando `isOpen` cambia + listeners
 * `scroll` (capture true) y `resize` con `requestAnimationFrame`. Evita
 * layout thrashing y re-render por pixel. Cleanup automático en unmount.
 *
 * **Decisión D V2** (click-outside portal-aware): listener global
 * `mousedown` que comprueba si el target NO es descendiente ni del
 * anchor ni del contenedor portal. Sin esto, el listener viejo
 * (`containerRef.contains(target)`) cierra el listbox al click dentro
 * del portal porque el portal NO es descendiente del container.
 *
 * **Decisión E V2** (z-index, decisión E V2): default 50 (consistente con modales). El
 * `ConfirmExtractionModal` del sistema usa también z-50; el portal y el
 * modal coexisten — cuando un modal está abierto los dropdowns
 * detrás están bloqueados a la interacción por el overlay del modal.
 *
 * **Decisión H V2** (cleanup robusto): los 3 listeners (scroll, resize,
 * mousedown) se limpian en el cleanup del efecto, garantizando que un
 * unmount mid-open o un fade-out no deja listeners colgados. Ningún
 * estado global (body styles) se modifica desde aquí.
 *
 * **Decisión K V2** (helper de testing): el hook
 * `usePortalAnchorPosition` se exporta como público para que tests
 * puedan razonar sobre la posición calculada si lo necesitan; el
 * componente `PortalDropdown` se exporta para tests anti-regresión
 * directos.
 *
 * **Decisión L V2** (viewport mid-open): el listener `resize` recoge
 * rotaciones móviles / cambios de viewport. En el caso desktop→mobile
 * el padre del dropdown alterna entre `CustomDropdown` (con portal) y
 * `NativeSelect` (sin portal); el switch desmonta el portal limpiamente.
 *
 * **Decisión M V2** (recalcular posición): se recalcula sólo en
 * (a) abrir, (b) scroll de cualquier antecesor (capture: true),
 * (c) resize de viewport. NUNCA en cada render — el efecto está atado
 * a `[isOpen, anchorRef]`.
 *
 * **Decisión N V2** (body styles): no se tocan los estilos del body.
 * El portal coexiste con modales sin acoplamiento.
 *
 * **SSR safety**: si `document` o `window` no existen (build SSR /
 * primera fase de hydration), el componente devuelve null y los hooks
 * salen temprano. No rompe el build de Vite porque `createPortal` es
 * sólo un import, sin side-effects.
 *
 * @module components/PortalDropdown
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

// ============================================================================
// HOOK DE POSICIONAMIENTO (decisión B V2 + decisión K V2 export)
// ============================================================================

export interface PortalAnchorPosition {
  /** Coordenada vertical del bottom del trigger (viewport coords). */
  top: number
  /** Coordenada horizontal del left del trigger (viewport coords). */
  left: number
  /** Ancho del trigger (para `width: ...` del portal). */
  width: number
}

/**
 * Hook que calcula la posición `fixed` de un popover alineado bajo un
 * trigger. Re-calcula al abrir + en scroll de cualquier antecesor
 * (capture: true) + en resize de la viewport. Sin abrir, devuelve null.
 *
 * Implementa (decisión B V2) y (decisión M V2): requestAnimationFrame
 * para evitar layout thrashing en scroll continuo.
 */
export function usePortalAnchorPosition(
  anchorRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
): PortalAnchorPosition | null {
  const [pos, setPos] = useState<PortalAnchorPosition | null>(null)

  useLayoutEffect(() => {
    if (!isOpen) {
      setPos(null)
      return
    }
    if (typeof window === 'undefined') return

    let raf = 0
    const compute = () => {
      const el = anchorRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setPos({ top: r.bottom, left: r.left, width: r.width })
    }

    // Cálculo síncrono inicial — antes del primer paint del listbox.
    compute()

    const onScrollOrResize = () => {
      if (raf) cancelAnimationFrame(raf)
      raf = requestAnimationFrame(compute)
    }

    // capture:true para capturar scroll de TODOS los antecesores
    // (incluido el card con `overflow-hidden`). Sin capture el evento
    // sólo dispararía si el window scrollea, no si scrollea un padre.
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)

    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [isOpen, anchorRef])

  return pos
}

// ============================================================================
// COMPONENTE PortalDropdown (decisión A V2 + D V2 + E V2 + H V2)
// ============================================================================

export interface PortalDropdownProps {
  /** Ref al elemento trigger (botón / input + chips). */
  anchorRef: RefObject<HTMLElement | null>
  /** Si está abierto. */
  isOpen: boolean
  /** Cierra el dropdown desde el padre. Llamado en click-outside. */
  onClose: () => void
  /** Contenido del listbox. */
  children: ReactNode
  /** Clase del wrapper externo del portal (sin afectar position/top/left/width). */
  className?: string
  /** Estilos extra mergeados al wrapper. */
  style?: CSSProperties
  /** Si true, el wrapper hereda el ancho exacto del anchor. Default true. */
  matchAnchorWidth?: boolean
  /** Píxeles de gap vertical entre el bottom del anchor y el top del portal. Default 4. */
  offsetTop?: number
  /** Z-index del wrapper portal. Default 50. */
  zIndex?: number
  /** ARIA label opcional del wrapper (no del listbox interno). */
  'aria-label'?: string
}

/**
 * Renderiza children en `document.body` con `position: fixed` alineados
 * bajo el `anchorRef.current`. Decisiones A V2, B V2, D V2, E V2, H V2,
 * K V2, L V2, M V2, N V2.
 */
export function PortalDropdown({
  anchorRef,
  isOpen,
  onClose,
  children,
  className,
  style,
  matchAnchorWidth = true,
  offsetTop = 4,
  zIndex = 50,
  'aria-label': ariaLabel,
}: PortalDropdownProps) {
  const portalRef = useRef<HTMLDivElement>(null)
  const pos = usePortalAnchorPosition(anchorRef, isOpen)

  // Click-outside portal-aware (decisión D V2).
  useEffect(() => {
    if (!isOpen) return
    if (typeof document === 'undefined') return
    const handle = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (!target) return
      // El target está dentro del trigger → ignoramos (el trigger
      // alterna isOpen por sí mismo).
      if (anchorRef.current && anchorRef.current.contains(target)) return
      // El target está dentro del portal → no cerramos (interacciones
      // internas: filtro, opciones, etc).
      if (portalRef.current && portalRef.current.contains(target)) return
      onClose()
    }
    // mousedown (no click) para que se ejecute antes que blur del input
    // dentro del portal — coherente con el patrón existente del proyecto.
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [isOpen, onClose, anchorRef])

  // SSR / no abierto → no renderizamos nada.
  if (!isOpen) return null
  if (typeof document === 'undefined') return null

  const wrapperStyle: CSSProperties = {
    position: 'fixed',
    top: pos ? pos.top + offsetTop : -9999,
    left: pos ? pos.left : -9999,
    width: pos && matchAnchorWidth ? pos.width : undefined,
    zIndex,
    // Visibility hidden hasta que tengamos posición real para evitar
    // un flash en la esquina (0, 0) en el primer paint. En jsdom el
    // bounding rect es {0,0} pero igual se calcula correctamente.
    visibility: pos ? 'visible' : 'hidden',
    ...style,
  }

  return createPortal(
    <div
      ref={portalRef}
      className={className}
      style={wrapperStyle}
      aria-label={ariaLabel}
      data-portal-dropdown=""
    >
      {children}
    </div>,
    document.body,
  )
}
